import React from 'react';

import { FaDownload, FaFilePdf } from 'react-icons/fa';
import '../assets/components/_cv.scss';

// Import DCInject images
import dcinjectBlockDiagram from '../assets/images/DCIject/DCInject-block-diagram.png';
import attackResult from '../assets/images/DCIject/attack-result.png';
import defenseResult from '../assets/images/DCIject/Defense-result.png';

const CV = () => {
  const handleDownloadCV = () => {
    const cvUrl = 'https://drive.google.com/file/d/1qpVyarW5wTtXUd9LVJ1tsROLW6WoL2iu/view?usp=sharing';
    const link = document.createElement('a');
    link.href = cvUrl;
    link.download = 'Nahom_M_Birhan_CV.pdf';
    link.target = '_blank';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="cv-container">
      <div className="content-wrapper">
        {/* Header Section */}
        <div className="cv-header">
          <div className="header-content">
            <h1>Curriculum Vitae</h1>
            <p>AI Security Researcher & Machine Learning Engineer</p>
            <div className="cv-download-section">
              <button onClick={handleDownloadCV} className="btn btn-primary cv-download-btn">
                <FaFilePdf className="btn-icon" />
                Download CV (PDF)
                <FaDownload className="btn-icon" />
              </button>
            </div>
          </div>
        </div>

        {/* Publications Section */}
        <section className="cv-section">
          <h2>Publications</h2>
          <div className="experience-card">
            <div className="card-header">
              <div className="company-info">
                <h3>DCInject: Persistent Backdoor Attacks via Frequency Manipulation in Personalized Federated Learning</h3>
                <div className="meta-info">
                  <span>International Conference on Acoustics, Speech and Signal Processing (ICASSP 2026)</span>
                </div>
              </div>
              <span className="role-badge">Accepted</span>
            </div>
            <p style={{ marginTop: '1rem', lineHeight: '1.6', color: '#4a5568' }}>
              <strong>Abstract:</strong> Personalized federated learning (PFL) creates client-specific models to handle data heterogeneity. Previously, PFL has been shown to be naturally resistant to backdoor attack propagation across clients. In this work, we reveal that PFL remains vulnerable to backdoor attacks through a novel frequency-domain approach. We propose DCInject, an adaptive frequency-domain backdoor attack for PFL, which removes portions of the zero-frequency component and replaces them with Gaussian-distributed samples in the frequency domain. Our attack achieves superior attack success rates while maintaining clean accuracy across four datasets (CIFAR-10/100, GTSRB, SVHN) compared to existing spatial-domain attacks, evaluated under parameter decoupling-based personalization.
            </p>
            <div className="publication-images" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginTop: '1.5rem' }}>
              <div style={{ textAlign: 'center' }}>
                <img src={dcinjectBlockDiagram} alt="DCInject Block Diagram" style={{ width: '100%', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                <p style={{ fontSize: '0.85rem', color: '#718096', marginTop: '0.5rem' }}>Block Diagram</p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <img src={attackResult} alt="Attack Result" style={{ width: '100%', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                <p style={{ fontSize: '0.85rem', color: '#718096', marginTop: '0.5rem' }}>Attack Results</p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <img src={defenseResult} alt="Defense Result" style={{ width: '100%', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                <p style={{ fontSize: '0.85rem', color: '#718096', marginTop: '0.5rem' }}>Defense Results</p>
              </div>
            </div>
          </div>
        </section>

        {/* Experience Timeline */}
        <section className="experience-section">
          <h2>Professional Experience</h2>
          <div className="timeline">
            <div className="timeline-item">
              <div className="timeline-marker"></div>
              <div className="timeline-content">
                <div className="experience-card">
                  <div className="card-header">
                    <div className="company-info">
                      <h3>Old Dominion University</h3>
                      <div className="meta-info">
                        <span>Norfolk, VA, USA</span>
                        <span>August 2024 — Present</span>
                      </div>
                      <h4>Research Assistant</h4>
                    </div>
                    <span className="role-badge">Research</span>
                  </div>
                  <ul className="achievements">
                    <li>Researching LLM security, jailbreak and backdoor attacks, and robust AI systems</li>
                    <li>Developing post-training defense mechanisms for language models</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker"></div>
              <div className="timeline-content">
                <div className="experience-card">
                  <div className="card-header">
                    <div className="company-info">
                      <h3>Cardinal Peak</h3>
                      <div className="meta-info">
                        <span>Lafayette, CO, USA</span>
                        <span> June 2022 — June 2024</span>
                      </div>
                      <h4>Embedded Software and ML Engineer</h4>
                    </div>
                    <span className="role-badge">Industry</span>
                  </div>

                  <div className="key-projects">
                    <div className="project-highlight">
                      <h5>CS Investigations Camera Platform</h5>
                      <p>Full-stack camera management system with secure authentication and real-time processing</p>
                      <div className="tech-stack">
                        <span>Flask</span>
                        <span>ReactJS</span>
                        <span>PostgreSQL</span>
                        <span>Docker</span>
                        <span>OpenCV</span>
                      </div>
                    </div>
                    <div className="project-highlight">
                      <h5>Dumpster Fullness Detection</h5>
                      <p>End-to-end ML pipeline for computer vision-based waste monitoring</p>
                      <div className="tech-stack">
                        <span>AWS DeepLens</span>
                        <span>SageMaker</span>
                        <span>TensorFlow</span>
                        <span>CNN</span>
                      </div>
                    </div>
                    <div className="project-highlight">
                      <h5>EdgeML Projects</h5>
                      <p>Multiple TinyML implementations for edge devices including digit recognition and environmental monitoring</p>
                      <div className="tech-stack">
                        <span>Arduino</span>
                        <span>TensorFlow Lite</span>
                        <span>Edge Impulse</span>
                        <span>IoT</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* timeline items for other roles */}
            <div className="timeline-item">
              <div className="timeline-marker"></div>
              <div className="timeline-content">
                <div className="experience-card">
                  <div className="card-header">
                    <div className="company-info">
                      <h3>Carnegie Mellon University Africa</h3>
                      <div className="meta-info">
                        <span>Kigali, Rwanda</span>
                        <span>Sep 2021 — May 2022</span>
                      </div>
                      <h4>Teaching Assistant</h4>
                      <h6>Data Inference and Applied Machine Learning  &</h6>
                      <h6>AI System Design</h6>

                    </div>
                    <span className="role-badge">Academic</span>
                  </div>
                  <ul className="achievements">
                    <li>Delivered recitations, developed assignments, and provided constructive feedback through grading</li>
                    <li>Held office hours and assisted with hands-on exercises, offering individualized support on complex topics</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker"></div>
              <div className="timeline-content">
                <div className="experience-card">
                  <div className="card-header">
                    <div className="company-info">
                      <h3>Carnegie Mellon University Africa</h3>
                      <div className="meta-info">
                        <span>Kigali, Rwanda</span>
                        <span>June 2021 — Sep 2021</span>
                      </div>
                      <h4>Graduate Student Intern</h4>
                    </div>
                    <span className="role-badge">Research</span>
                  </div>
                  <ul className="achievements">
                    <li>Developed and evaluated ML-based algorithm for network anomaly detection using LSTM and Savitzky-Golay filter</li>
                    <li>Tested on various datasets, analyzed performance, and authored scientific reports</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker"></div>
              <div className="timeline-content">
                <div className="experience-card">
                  <div className="card-header">
                    <div className="company-info">
                      <h3>Symbol Technologies PLC</h3>
                      <div className="meta-info">
                        <span>Addis Ababa, Ethiopia</span>
                        <span>Nov 2019 — Aug 2020</span>
                      </div>
                      <h4>Network and System Engineer</h4>
                    </div>
                    <span className="role-badge">Industry</span>
                  </div>
                  <ul className="achievements">
                    <li>Designed and deployed a complete VISP solution with custom server/router infrastructure</li>
                    <li>Managed FreePBX-based call center system and implemented IVR menus</li>
                    <li>Developed mass SMS delivery system for virtual internet service customers</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker"></div>
              <div className="timeline-content">
                <div className="experience-card">
                  <div className="card-header">
                    <div className="company-info">
                      <h3>Huawei</h3>
                      <div className="meta-info">
                        <span>Addis Ababa, Ethiopia</span>
                        <span>Mar 2018 — Aug 2018</span>
                      </div>
                      <h4>Network Engineer Intern</h4>
                    </div>
                    <span className="role-badge">Internship</span>
                  </div>
                  <ul className="achievements">
                    <li>Designed and configured campus-wide network solutions with VLAN segmentation</li>
                    <li>Evaluated Ethio-telecom's cellular network and contributed to solution strategies</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Education Section */}
        <section className="education-section">
          <h2>Education</h2>
          <div className="education-grid">
            <div className="education-card">
              <h3>PhD in Computer Science</h3>
              <span className="school">Old Dominion University (ODU)</span>
              <span className="edu-date">August 2024 — Present</span>
              <p>Focus: AI Security, LLM Security, and Robust AI Systems,Privacy</p>
            </div>

            <div className="education-card">
              <h3>M.Sc. Electrical & Computer Engineering</h3>
              <span className="school">Carnegie Mellon University (CMU)</span>
              <span className="edu-date">May 2022</span>
              <p>Focus: Machine Learning (ML) and Cyber Security</p>
            </div>


   
            <div className="education-card">
              <h3>B.Sc. Electrical & Computer Engineering</h3>
              <span className="school">Addis Ababa University (AAU)</span>
              <span className="edu-date">July 2019</span>
              <p>Focus: Electronics and Communication Engineering</p>
         
            </div>
          </div>

        </section>

        {/* Achievements & Volunteer Section */}
        <section className="achievements-section">
          <h2>Achievements & Volunteer</h2>
          <ul className="achievements-list">
            <li>Program Committee Member (Artifact Evaluation), ACSAC 2025</li>
            <li>MasterCard Foundation Scholarship (M.Sc.)</li>
            <li>Very great distinction certificate (B.Sc.)</li>
            <li>Python programming instructor for high school students at Meles Foundation and Addis Coder</li>
            <li>Tutored applied mathematics for engineering students at AAU as a volunteer</li>
            <li>Founder and Chairperson, "Hagerihin Ewok" Club: Led student organization promoting national heritage awareness</li>
          </ul>
        </section>
        <div className="cv-download-section">
              <button onClick={handleDownloadCV} className="btn btn-primary cv-download-btn">
                <FaFilePdf className="btn-icon" />
                Download CV (PDF)
                <FaDownload className="btn-icon" />
              </button>
            </div>

      </div>
    
    </div>
  );
};

export default CV;




